import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import IndustrialProduct from "./components/IndustrialProduct";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./Herramienta.css";

/* ============================================================
   API
============================================================ */

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER CATÁLOGO DE HERRAMIENTAS
============================================================ */

const obtenerCatalogoHerramientas = async () => {
  const respuesta = await axios.get(API_URL, {
    timeout: 45000,
  });

  return respuesta.data || {};
};

/* ============================================================
   COMPONENTE
============================================================ */

function Herramienta() {
  /* ============================================================
     FILTROS
  ============================================================ */

  const [filtroSeleccionado, setFiltroSeleccionado] = useState("todos");

  const [filtroBusqueda, setFiltroBusqueda] = useState("");

  /* ============================================================
     PAGINACIÓN
  ============================================================ */

  const [paginaActual, setPaginaActual] = useState(1);

  const productosPorPagina = 16;

  /* ============================================================
     TANSTACK QUERY + INDEXEDDB
  ============================================================ */

  const {
    data: catalogo = {},
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["catalogo-herramientas"],
    queryFn: obtenerCatalogoHerramientas,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (isError) {
    console.error("Error al obtener herramientas:", error);
  }

  /* ============================================================
     SCROLL PAGINACIÓN
  ============================================================ */

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [paginaActual]);

  /* ============================================================
     NORMALIZAR TEXTO
  ============================================================ */

  const normalizarTexto = (texto = "") => {
    return String(texto)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  /* ============================================================
     BÚSQUEDA
  ============================================================ */

  const coincideBusqueda = (item) => {
    if (!filtroBusqueda.trim()) {
      return true;
    }

    const palabras = normalizarTexto(filtroBusqueda)
      .split(/\s+/)
      .filter(Boolean);

    const textoProducto = normalizarTexto(
      `${item?.nombre || ""} ${item?.marca || ""} ${
        item?.modelo || ""
      } ${item?.codigoProducto || ""} ${item?.descripcion || ""}`,
    );

    return palabras.every((palabra) => textoProducto.includes(palabra));
  };

  /* ============================================================
     CATEGORÍAS
  ============================================================ */

  const categorias = useMemo(
    () => ({
      manometer: catalogo.manometers || [],
      multimetro: catalogo.multimetros || [],
      bomba: catalogo.bombasVacio || [],
      expansor: catalogo.expansores || [],
      cortador: catalogo.cortadores || [],
      acampanador: catalogo.acampanadores || [],
      vacumetro: catalogo.vacumetros || [],
      pico: catalogo.picos || [],
    }),
    [catalogo],
  );

  /* ============================================================
     TODOS LOS PRODUCTOS
  ============================================================ */

  const todosLosProductos = useMemo(() => {
    return Object.values(categorias).flat();
  }, [categorias]);

  /* ============================================================
     PRODUCTOS FILTRADOS
  ============================================================ */

  const productosFiltrados = useMemo(() => {
    const resultado = {};

    Object.entries(categorias).forEach(([categoria, productos]) => {
      resultado[categoria] = productos.filter(coincideBusqueda);
    });

    resultado.todos = todosLosProductos.filter(coincideBusqueda);

    return resultado;
  }, [categorias, todosLosProductos, filtroBusqueda]);

  /* ============================================================
     NOMBRES
  ============================================================ */

  const nombresLegibles = {
    todos: "Todas las herramientas",
    manometer: "Manómetros",
    multimetro: "Pinzas Amperimétricas",
    bomba: "Bombas de Vacío",
    expansor: "Expansoras de Cañería",
    cortador: "Cortadoras de Cañería",
    acampanador: "Acampanadoras",
    vacumetro: "Vacuómetros",
    pico: "Picos para Soldar",
  };

  /* ============================================================
     TÍTULOS
  ============================================================ */

  const titulosCategoria = {
    todos: "Equipos y Herramientas de Refrigeración",
    manometer: "Manómetros",
    multimetro: "Pinzas Amperimétricas",
    bomba: "Bombas de Vacío",
    expansor: "Expansoras de Cañerías de Cobre",
    cortador: "Cortadoras de Cañerías de Cobre",
    acampanador: "Acampanadoras de Cañerías de Cobre",
    vacumetro: "Vacuómetros",
    pico: "Picos para Soldar Cañerías de Cobre",
  };

  /* ============================================================
     ICONOS
  ============================================================ */

  const iconosCategoria = {
    todos: "bi-grid",
    manometer: "bi-speedometer2",
    multimetro: "bi-lightning-charge",
    bomba: "bi-fan",
    expansor: "bi-arrows-angle-expand",
    cortador: "bi-scissors",
    acampanador: "bi-tools",
    vacumetro: "bi-gauge",
    pico: "bi-fire",
  };

  const obtenerTituloCategoria = () =>
    titulosCategoria[filtroSeleccionado] || titulosCategoria.todos;

  /* ============================================================
     RESULTADOS
  ============================================================ */

  const productosCategoria = productosFiltrados[filtroSeleccionado] || [];

  const totalProductos = todosLosProductos.length;

  const productosMostrados = productosCategoria.length;

  /* ============================================================
     PRODUCTOS DE LA PÁGINA ACTUAL
  ============================================================ */

  const productosEnPagina =
    filtroSeleccionado === "todos"
      ? productosCategoria.slice(
          (paginaActual - 1) * productosPorPagina,
          paginaActual * productosPorPagina,
        )
      : productosCategoria;

  const totalPaginas =
    filtroSeleccionado === "todos"
      ? Math.ceil(productosCategoria.length / productosPorPagina)
      : 1;

  /* ============================================================
     CAMBIAR CATEGORÍA
  ============================================================ */

  const seleccionarCategoria = (categoria) => {
    setFiltroSeleccionado(categoria);
    setFiltroBusqueda("");
    setPaginaActual(1);
  };

  /* ============================================================
     CAMBIAR PÁGINA
  ============================================================ */

  const cambiarPagina = (pagina) => {
    if (pagina < 1 || pagina > totalPaginas) {
      return;
    }

    setPaginaActual(pagina);
  };

  /* ============================================================
     VISTA
  ============================================================ */

  return (
    <div className="herramienta-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="herramienta-hero">
          <div className="herramienta-container">
            <div className="herramienta-hero-card">
              <div>
                <span className="herramienta-kicker">
                  CATÁLOGO G&amp;J FRÍO
                </span>

                <h1>{obtenerTituloCategoria()}</h1>

                <p>
                  Encuentra equipos y herramientas para instalación,
                  diagnóstico, mantenimiento y reparación de sistemas de
                  refrigeración y aire acondicionado.
                </p>
              </div>

              <div className="herramienta-hero-icon">
                <i className="bi bi-tools"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            TOOLBAR
        ===================================================== */}

        <section className="herramienta-toolbar-section">
          <div className="herramienta-container">
            <div className="herramienta-toolbar">
              <div className="herramienta-toolbar-left">
                <button
                  type="button"
                  className="herramienta-category-button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#herramientaCategories"
                  aria-controls="herramientaCategories"
                >
                  <i className="bi bi-grid-fill"></i>

                  <span>Categorías</span>

                  <i className="bi bi-chevron-right"></i>
                </button>

                <div className="herramienta-current-category">
                  <span>Categoría actual</span>

                  <strong>{nombresLegibles[filtroSeleccionado]}</strong>
                </div>
              </div>

              <div className="herramienta-counter">
                <span>Mostrando</span>

                <strong>{!isLoading ? productosMostrados : 0}</strong>

                <span>de</span>

                <strong>{!isLoading ? totalProductos : 0}</strong>

                <span>{totalProductos === 1 ? "artículo" : "artículos"}</span>
              </div>
            </div>

            {/* =================================================
                BUSCADOR
            ================================================= */}

            <div className="herramienta-search-row">
              <div className="herramienta-search">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  placeholder="Buscar por nombre, marca, modelo o código..."
                  value={filtroBusqueda}
                  onChange={(e) => {
                    setFiltroBusqueda(e.target.value);
                    setPaginaActual(1);
                  }}
                />

                {filtroBusqueda && (
                  <button
                    type="button"
                    className="herramienta-search-clear"
                    onClick={() => {
                      setFiltroBusqueda("");
                      setPaginaActual(1);
                    }}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            OFFCANVAS
        ===================================================== */}

        <div
          className="offcanvas offcanvas-start herramienta-offcanvas"
          data-bs-scroll="true"
          tabIndex="-1"
          id="herramientaCategories"
          aria-labelledby="herramientaCategoriesLabel"
        >
          <div className="offcanvas-header herramienta-offcanvas-header">
            <div>
              <span>CATÁLOGO</span>

              <h5 className="offcanvas-title" id="herramientaCategoriesLabel">
                Subcategorías
              </h5>
            </div>

            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Cerrar"
            ></button>
          </div>

          <div className="offcanvas-body herramienta-offcanvas-body">
            <div className="herramienta-category-list">
              {Object.keys(nombresLegibles).map((categoria) => {
                const cantidad = productosFiltrados[categoria]?.length || 0;

                return (
                  <button
                    type="button"
                    key={categoria}
                    className={`herramienta-category-item ${
                      filtroSeleccionado === categoria ? "active" : ""
                    }`}
                    onClick={() => seleccionarCategoria(categoria)}
                    data-bs-dismiss="offcanvas"
                  >
                    <span className="herramienta-category-item-icon">
                      <i className={`bi ${iconosCategoria[categoria]}`}></i>
                    </span>

                    <span className="herramienta-category-item-text">
                      <strong>{nombresLegibles[categoria]}</strong>

                      <small>
                        {!isLoading ? cantidad : 0}{" "}
                        {cantidad === 1 ? "producto" : "productos"}
                      </small>
                    </span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* =====================================================
            PRODUCTOS
        ===================================================== */}

        <section className="herramienta-products-section">
          <div className="herramienta-container">
            {/* =================================================
                GROWING SPINNER
            ================================================= */}

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  minHeight: "100px",
                }}
              >
                <div
                  className="spinner-grow text-primary"
                  role="status"
                  style={{
                    width: "1.6rem",
                    height: "1.6rem",
                  }}
                >
                  <span className="visually-hidden">
                    Cargando herramientas...
                  </span>
                </div>
              </div>
            ) : productosEnPagina.length > 0 ? (
              /* ===============================================
                 PRODUCTOS
              =============================================== */

              <div className="herramienta-products-grid">
                {productosEnPagina.map((item) => (
                  <IndustrialProduct
                    key={item._id}
                    img={item.imagen}
                    title={item.nombre}
                    id={item._id}
                    details={[
                      {
                        label: "Marca",
                        value: item.marca,
                      },

                      ...(item.modelo
                        ? [
                            {
                              label: "Modelo",
                              value: item.modelo,
                            },
                          ]
                        : []),

                      {
                        label: "Código Producto",
                        value: item.codigoProducto,
                      },

                      {
                        label: "Descripción",
                        value: item.descripcion,
                      },
                    ]}
                  />
                ))}
              </div>
            ) : (
              /* ===============================================
                 SIN RESULTADOS
              =============================================== */

              <div className="herramienta-empty">
                <div className="herramienta-empty-icon">
                  <i className="bi bi-search"></i>
                </div>

                <h3>No encontramos herramientas</h3>

                <p>
                  No existen coincidencias para la búsqueda o categoría
                  seleccionada.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setFiltroBusqueda("");
                    setFiltroSeleccionado("todos");
                    setPaginaActual(1);
                  }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                  Mostrar todos
                </button>
              </div>
            )}

            {/* =================================================
                PAGINACIÓN
            ================================================= */}

            {!isLoading &&
              filtroSeleccionado === "todos" &&
              totalPaginas > 1 && (
                <nav
                  className="herramienta-pagination-wrapper"
                  aria-label="Paginación de herramientas"
                >
                  <button
                    type="button"
                    className="herramienta-pagination-arrow"
                    disabled={paginaActual === 1}
                    onClick={() => cambiarPagina(paginaActual - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>

                    <span>Anterior</span>
                  </button>

                  <div className="herramienta-pagination-pages">
                    {Array.from(
                      {
                        length: totalPaginas,
                      },
                      (_, index) => index + 1,
                    ).map((pagina) => (
                      <button
                        type="button"
                        key={pagina}
                        className={`herramienta-page-button ${
                          paginaActual === pagina ? "active" : ""
                        }`}
                        onClick={() => cambiarPagina(pagina)}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="herramienta-pagination-arrow"
                    disabled={paginaActual === totalPaginas}
                    onClick={() => cambiarPagina(paginaActual + 1)}
                  >
                    <span>Siguiente</span>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                </nav>
              )}
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}

export default Herramienta;
